import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import AttachmentTypeModel from '../../models/AttachmentTypeModel';
import CodeTaskParameterInfo from '../../models/CodeTaskParameterInfo';
import WorkflowTaskAddDocumentModel from '../../models/WorkflowTaskAddDocumentModel';
import WorkflowTaskDefinitionModel from '../../models/WorkflowTaskDefinitionModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import WorkflowTaskControl from './Controls/WorkflowTaskControl';

const WorkflowTaskDefinitionView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowTaskDefinitionModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const navigate = useNavigate()

    const [formData, setFormData] = useState<WorkflowTaskDefinitionModel>();

    let idParams: IdParams = useParams();
    let workflowTaskDefinitionId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        workflowTaskDefinitionId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (workflowTaskDefinitionId !== undefined) {
            DataService.getById<WorkflowTaskDefinitionModel>('api/v1/WorkflowTaskDefinitions/', workflowTaskDefinitionId)
                .then(response => {
                    console.log('task defintion', response)
                    setFormData(response)
                })
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [workflowTaskDefinitionId]);

    function submit() {
        if (formData) {
            DataService
                .update<WorkflowTaskDefinitionModel>(`api/v1/WorkflowTaskDefinitions/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function onFieldChanged(name: string, value: string) {
        if (formData) {
            if (name === 'attachmentCount') {
                const count = parseInt(value);
                if (count > formData.documents.length) {
                    for (let i = formData.documents.length; i < count; i++) {
                        formData.documents.push({ attachmentType: { name: '' } as AttachmentTypeModel } as WorkflowTaskAddDocumentModel);
                    }
                }

                if (count < formData.documents.length) {
                    formData.documents.splice(count, formData.documents.length - count);
                }
            }
        }
    }

    useEffect(() => {
        getCodeTaskParameterInfo(formData?.codeTaskName)
    }, [formData?.codeTaskName])

    function getCodeTaskParameterInfo(codeTaskName?: string) {
        if (formData && codeTaskName) {
            DataService
                .get<CodeTaskParameterInfo[]>(`api/v1/CodeTaskParameterInfos/ByCodeTaskName/${codeTaskName}`)
                .then(response => {
                    if (!formData.codeTaskParameterInfos)
                        formData.codeTaskParameterInfos = []

                    for (let i = 0; i < response.length; i++) {
                        for (let j = 0; j < formData.codeTaskParameterInfos.length; j++) {
                            if (response[i].name == formData.codeTaskParameterInfos[j].name)
                                response[i].value = formData.codeTaskParameterInfos[j].value
                        }
                    }
                    formData.codeTaskParameterInfos = response

                    //console.log('formData', formData)
                    setFormData(() => { return { ...formData } })
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'taskOrder', label: 'Task priority', labelSize: 5, validationRules: [{ type: 'isRequiredNumber' }] },
            { name: 'taskTypeId', label: 'Task type', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'taskCategoryId', label: 'Task category', labelSize: 5 },
            { name: 'initialTaskStatusId', label: 'Initial status', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'assignToFunctionalRoleId', label: 'Assigned to a', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'escalateToFunctionalRoleId', label: 'Escalate to a', labelSize: 5 },
            { name: 'isRequired', label: 'Required', labelSize: 5 },
            { name: 'forwardsWorkflow', label: 'Forwards the workflow after completion', labelSize: 5 },
            { name: 'duration', label: 'Duration', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'reminderInterval', label: 'Reminder interval', labelSize: 5 },
            { name: 'description', label: 'Description', labelSize: 5 },
            { name: 'businessPartnerTypeId', label: 'Business-partner type' },
            { name: 'attachmentTypeId', label: 'Attachment type' },
            { name: 'attachmentRequired', label: 'Attachment required' },
            { name: 'title', label: 'Title' },
            { name: 'userDefinedFieldsIds', label: 'Custom fields' },
            { name: 'codeTaskName', label: 'Code-task' },
            { name: 'parameters', label: 'Parameters' },
            { name: 'attachmentCount', label: 'Nr of Attachments', labelSize: 5 },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    if (formData?.codeTaskParameterInfos) {
        formData.codeTaskParameterInfos.forEach((parInfo, index) => {
            formConfig.fields.push({ name: `codeTaskParameterInfos.${index}.value`, label: parInfo.name })
        })
    }

    if (formData?.documents) {
        formData.documents.forEach((doc, index) => {
            formConfig.fields.push({ name: `documents.${index}.attachmentTypeId`, label: 'Type', validationRules: [{ type: 'isRequired' }] })
            formConfig.fields.push({ name: `documents.${index}.attachmentRequired`, label: 'Required' })
        })
    }


    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!workflowTaskDefinitionId || !formData)
        return <SpinnerOverlay />

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.ViewWorkflowDefinition))
        return (<Unauthorized />);

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={275} icon="fa fa-gears" title="GoTo workflow definition" onClick={() => navigate('/plm/WorkflowSetup/Workflow/' + formData.workflowStepDefinition.workflowDefinitionId)} />
            <ActionMenuItem icon="fa fa-gears" title="GoTo workflow step definition" onClick={() => navigate('/plm/WorkflowSetup/WorkflowStep/' + formData.workflowStepDefinitionId)} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Workflow task definition' actions={formActions} footer={(
                <Row>
                    <Col md='6'>
                        <ButtonBar offset={5}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateWorkflowDefinition} workflowDefinitionId={formData.workflowStepDefinition.workflowDefinitionId}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                    <Col md='6'>
                        <ButtonBar offset={0}>
                        </ButtonBar>
                    </Col>

                </Row>
            )}>
                <WorkflowTaskControl getFieldProps={getFieldProps} formData={formData} workflowDefinitionId={formData.workflowStepDefinition.workflowDefinitionId} />
            </Form>
        </div>
    );
}
export default WorkflowTaskDefinitionView;